import { FieldMetadata, useForm } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod";
import { ActionFunctionArgs, json } from "@remix-run/node";
import { Form, useFetcher, useNavigate } from "@remix-run/react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Asterisk } from "lucide-react";
import { useEffect, useState } from "react";
import { fetchDistricts } from "~/classes/lookups";
import { GetStaticLookup } from "~/classes/staticLookup";
import { getAboutYouDefault, GetUserAtom } from "~/classes/user";
import { ComboboxConform } from "~/components/conform/Combobox";
import { InputConform } from "~/components/conform/Input";
import { RadioGroupConform } from "~/components/conform/RadioGroup";
import { SelectConform } from "~/components/conform/Select";
import { Field, FieldError } from "~/components/field";
import { FormButtons } from "~/components/form-submit-buttons";
import { Label } from "~/components/ui/label";
import { useToast } from "~/components/ui/use-toast";
import { useErrorToast } from "~/hooks/customToasts";
import { parseHtmlWithLinks } from "~/lib/html-parser";
import {
	aboutYouAtom,
	aboutDriverAtom,
	retainDriverData,
} from "~/lib/jotai/bb/collision-other-vehicle";
import { isLoadingAtom } from "~/lib/jotai/loading";
import { isPolicyHolderOrProxy } from "~/lib/utils";
import { aboutYouValidator } from "~/lib/zod/bb/motor/collision-other-vehicle/validator";
import { ClaimTypes, getEnumKey } from "~/models/claimTypes";
import {
	bbInsuranceProviderTypes,
	ReportingPartyTypes,
} from "~/models/dropdowns";
import { Regions } from "~/models/region";
import { validateRegionAccess } from "~/lib/middleware/region";
import { useParishSubmit } from "~/hooks/fetcherSubmit";

export async function loader() {
	validateRegionAccess(Regions.Barbados.code);

	return null;
}

export async function action({ request }: ActionFunctionArgs) {
	const { type, body } = await request.json();

	switch (type) {
		case "districts": {
			const districts = await fetchDistricts(body["parish"]);
			return districts;
		}
	}

	return json({ success: true });
}

export default function AboutYou() {
	const [aboutYouData, setAboutYouData] = useAtom(aboutYouAtom);
	const [aboutDriverData, setAboutDriverData] = useAtom(aboutDriverAtom);
	const retainDriverDetails = useAtomValue(retainDriverData);
	const setLoading = useSetAtom(isLoadingAtom);
	const { toast } = useToast();
	const { errorToast } = useErrorToast();

	const navigate = useNavigate();
	const user = GetUserAtom();
	const loaderData = GetStaticLookup();

	const defaultValue = getAboutYouDefault(aboutYouData, user);

	const [form, fields] = useForm({
		id: "aboutYou",
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: aboutYouValidator });
		},
		defaultValue: { ...defaultValue },
		async onSubmit(e) {
			e.preventDefault();
			const form = e.currentTarget;
			const formData = new FormData(form);
			const result = parseWithZod(formData, { schema: aboutYouValidator });
			if (result.status === "success") {
				setLoading(true);
				if (result.value.PolicyNumber.startsWith("P")) {
					const response = await fetch("/validate", {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							policyNumber: result.value.PolicyNumber,
							lossType: getEnumKey(
								ClaimTypes,
								ClaimTypes.CollisionOtherVehicle
							),
							region: Regions.Barbados.name,
						}),
					});
					const validation = await response.json();
					if (!validation.isSuccess) {
						toast({
							variant: "destructive",
							title: "Validation Failed",
							description: parseHtmlWithLinks(validation.message),
							duration: 7000,
						});
						setLoading(false);
						return;
					}
				}

				const isPolicyHolder = isPolicyHolderOrProxy(
					result.value.partyReportingIncident
				)
					? true
					: false;

				if (!retainDriverDetails && result.value.AreYouDriver === "no") {
					setAboutDriverData({
						AreYouPolicyHolder: isPolicyHolder ? "yes" : "no",
						AreYouDriver: result.value.AreYouDriver,
					});
				} else if (result.value.AreYouDriver === "yes" && isPolicyHolder) {
					setAboutDriverData({
						...aboutDriverData,
						AreYouPolicyHolder: "yes",
						AreYouDriver: result.value.AreYouDriver,
						relationshipToHolder: "SELF",
						DriverFirstName: result.value.PolicyholderFirstName,
						DriverLastName: result.value.PolicyholderLastName,
						phoneType: result.value.ReporterPhoneType,
						DriverPhoneNumber: result.value.reportingPhoneNumber,
						emailType: result.value.ReporterEmailType,
						DriverEmailAddress: result.value.reportingEmailAddress,
						DriverContactPreference:
							result.value.ReporterContactPreference,

						DriverAddressType: result.value.ReporterAddressType,
						DriverAddressLine1: result.value.ReporterAddressLine1,
						...(result.value.ReporterAddressLine2
							? {
									DriverAddressLine2:
										result.value.ReporterAddressLine2,
								}
							: {}),
						DriverCountry: result.value.ReporterCountry,

						...(result.value.ReporterParish
							? { DriverParish: result.value.ReporterParish }
							: {}),
						...(result.value.ReporterDistrict
							? { DriverDistrict: result.value.ReporterDistrict }
							: {}),

						//also autofill driver's license number if the id is a license

						...(result.value?.ReporterIdentificationType === "DL"
							? {
									LicenseNumber:
										result.value.ReporterIdentificationNumber,
								}
							: {}),
					});
				} else if (result.value.AreYouDriver === "yes" && !isPolicyHolder) {
					setAboutDriverData({
						...aboutDriverData,
						AreYouPolicyHolder: "no",
						AreYouDriver: result.value.AreYouDriver,
						relationshipToHolder: result.value.relationshipToHolder,
						DriverFirstName: result.value.ReporterFirstName,
						DriverLastName: result.value.ReporterLastName,
						phoneType: result.value.ReporterPhoneType,
						DriverPhoneNumber: result.value.reportingPhoneNumber,
						emailType: result.value.ReporterEmailType,
						DriverEmailAddress: result.value.reportingEmailAddress,
						DriverContactPreference:
							result.value.ReporterContactPreference,

						DriverAddressType: result.value.ReporterAddressType,
						DriverAddressLine1: result.value.ReporterAddressLine1,
						...(result.value.ReporterAddressLine2
							? {
									DriverAddressLine2:
										result.value.ReporterAddressLine2,
								}
							: {}),
						DriverCountry: result.value.ReporterCountry,

						...(result.value.ReporterParish
							? { DriverParish: result.value.ReporterParish }
							: {}),
						...(result.value.ReporterDistrict
							? { DriverDistrict: result.value.ReporterDistrict }
							: {}),

						//also autofill driver's license number if the id is a license
						...(result.value?.ReporterIdentificationType === "DL"
							? {
									LicenseNumber:
										result.value.ReporterIdentificationNumber,
								}
							: {}),
					});
				} else {
					if (
						result.value.partyReportingIncident !=
							aboutYouData.partyReportingIncident ||
						result.value.AreYouDriver != aboutYouData.AreYouDriver
					) {
						// eslint-disable-next-line @typescript-eslint/no-unused-vars
						const { gaveDriverConsent, ...rest } = aboutDriverData;

						//reset gave driver consent if dependencies change
						setAboutDriverData({
							...rest,
							AreYouPolicyHolder: isPolicyHolder ? "yes" : "no",
							AreYouDriver: result.value.AreYouDriver,
						});
					} else {
						setAboutDriverData({
							...aboutDriverData,
							AreYouPolicyHolder: isPolicyHolder ? "yes" : "no",
							AreYouDriver: result.value.AreYouDriver,
						});
					}
				}

				setAboutYouData(result.value);
				navigate("../about-driver");
			}
		},
		shouldValidate: "onBlur",
		shouldRevalidate: "onInput",
	});

	const fetcher = useFetcher<typeof action>();

	const [districts, setDistricts] = useState<
		{ label: string; value: string }[]
	>([]);

	useEffect(() => {
		setLoading(false);
	}, []);

	useParishSubmit(fields, fetcher);

	useEffect(() => {
		if (fields.ReporterCountry.value === "barbados") {
			setDistricts(fetcher.data ?? []);
			if (fetcher.data?.length === 0) {
				errorToast({
					description: "Failed to load districts.",
				});
			}
		}
	}, [fetcher.data]);

	const countries = loaderData.countries?.map((country) => ({
		label: country.countryName,
		value: country.countryName.toLowerCase(),
	}));
	const jmParishes = loaderData.jmParishes?.map((parish) => ({
		name: parish.defaultValue,
		value: parish.code,
	}));

	const bbParishes = loaderData.bbParishes?.map((parish) => ({
		name: parish.defaultValue,
		value: parish.code,
	}));

	const idTypes = loaderData.bbIdTypes?.map((idType) => ({
		name: idType.defaultValue,
		value: idType.code,
	}));

	const addressTypes = loaderData.addressTypes?.map((addressType) => ({
		name: addressType.defaultValue,
		value: addressType.code,
	}));

	const emailTypes = loaderData.emailTypes?.map((emailType) => ({
		name: emailType.defaultValue,
		value: emailType.code,
	}));

	const phoneTypes = loaderData.phoneTypes?.map((phoneType) => ({
		name: phoneType.defaultValue,
		value: phoneType.code,
	}));

	const relationships = loaderData.relationships?.map((relationship) => ({
		name: relationship.defaultValue,
		value: relationship.code,
	}));

	//controlled components
	const [reporterFirstNameValue, setReporterFirstNameValue] = useState(() => {
		const val = isPolicyHolderOrProxy(fields.partyReportingIncident.value)
			? fields.PolicyholderFirstName.value
			: (aboutYouData.ReporterFirstName ?? "");

		return val;
	});
	const [reporterLastNameValue, setReporterLastNameValue] = useState(() => {
		const val = isPolicyHolderOrProxy(fields.partyReportingIncident.value)
			? fields.PolicyholderLastName.value
			: (aboutYouData.ReporterLastName ?? "");

		return val;
	});

	return (
		<Form method="POST" id={form.id} onSubmit={form.onSubmit} noValidate>
			<Field className="mb-4">
				<Label htmlFor={fields.partyReportingIncident.id}>
					Who is the person reporting the incident?
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<SelectConform
					meta={fields.partyReportingIncident}
					items={ReportingPartyTypes}
					handleChange={(value) => {
						if (value === "driver") {
							form.update({
								name: fields.AreYouDriver.name,
								value: "yes",
							});
						}
					}}
				/>
				{fields.partyReportingIncident.errors && (
					<FieldError>{fields.partyReportingIncident.errors}</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.PolicyNumber.id}>
					Policy Number
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<InputConform meta={fields.PolicyNumber} type="text" />
				{fields.PolicyNumber.errors && (
					<FieldError>{fields.PolicyNumber.errors}</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.PolicyholderFirstName.id}>
					Policyholder First Name
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<InputConform
					meta={fields.PolicyholderFirstName}
					type="text"
					onChange={(e) => {
						if (
							isPolicyHolderOrProxy(fields.partyReportingIncident.value)
						) {
							setReporterFirstNameValue(e.target.value);
						}
					}}
				/>
				{fields.PolicyholderFirstName.errors && (
					<FieldError>{fields.PolicyholderFirstName.errors}</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.PolicyholderLastName.id}>
					Policyholder Last Name
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<InputConform
					meta={fields.PolicyholderLastName}
					type="text"
					onChange={(e) => {
						if (
							isPolicyHolderOrProxy(fields.partyReportingIncident.value)
						) {
							setReporterLastNameValue(e.target.value);
						}
					}}
				/>
				{fields.PolicyholderLastName.errors && (
					<FieldError>{fields.PolicyholderLastName.errors}</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.insuranceProvider.id}>
					Who do you do Insurance with?
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>

				<ComboboxConform
					meta={fields.insuranceProvider}
					items={bbInsuranceProviderTypes}
				/>
				{fields.insuranceProvider.errors && (
					<FieldError>{fields.insuranceProvider.errors}</FieldError>
				)}
			</Field>

			<div className="mt-8">
				<Label className="text-slate-500">
					Enter details about the reporting party
				</Label>
			</div>

			<Field className="mb-4">
				<Label htmlFor={fields.ReporterFirstName.id}>
					Reporting Party First Name
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<InputConform
					meta={fields.ReporterFirstName}
					type="text"
					id="ReporterFirstName"
					value={reporterFirstNameValue}
					onChange={(e) => setReporterFirstNameValue(e.target.value)}
				/>
				{fields.ReporterFirstName.errors && (
					<FieldError>{fields.ReporterFirstName.errors}</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.ReporterLastName.id}>
					Reporting Party Last Name
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<InputConform
					meta={fields.ReporterLastName}
					type="text"
					value={reporterLastNameValue}
					onChange={(e) => setReporterLastNameValue(e.target.value)}
				/>
				{fields.ReporterLastName.errors && (
					<FieldError>{fields.ReporterLastName.errors}</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.relationshipToHolder.id}>
					Relationship to Insured
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<SelectConform
					meta={fields.relationshipToHolder}
					items={relationships}
					initialValue={
						isPolicyHolderOrProxy(fields.partyReportingIncident.value)
							? "SELF"
							: ""
					}
					dependency={
						isPolicyHolderOrProxy(fields.partyReportingIncident.value) ||
						Object.getOwnPropertyNames(aboutYouData).length != 0
					}
				/>
				{fields.relationshipToHolder.errors && (
					<FieldError>{fields.relationshipToHolder.errors}</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.ReporterIdentificationType.id}>
					Identification Type
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<SelectConform
					meta={fields.ReporterIdentificationType}
					items={idTypes}
				/>
				{fields.ReporterIdentificationType.errors && (
					<FieldError>
						{fields.ReporterIdentificationType.errors}
					</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.ReporterIdentificationNumber.id}>
					Identification Number
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<InputConform
					meta={fields.ReporterIdentificationNumber}
					type="text"
				/>
				{fields.ReporterIdentificationNumber.errors && (
					<FieldError>
						{fields.ReporterIdentificationNumber.errors}
					</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.ReporterPhoneType.id}>
					Phone Type
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<SelectConform
					meta={fields.ReporterPhoneType}
					items={phoneTypes}
					defaultValue={
						isPolicyHolderOrProxy(fields.partyReportingIncident.value)
							? aboutYouData.ReporterPhoneType
							: ""
					}
				/>
				{fields.ReporterPhoneType.errors && (
					<FieldError>{fields.ReporterPhoneType.errors}</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.reportingPhoneNumber.id}>
					Phone Number
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<InputConform meta={fields.reportingPhoneNumber} type="text" />
				{fields.reportingPhoneNumber.errors && (
					<FieldError>{fields.reportingPhoneNumber.errors}</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.ReporterEmailType.id}>
					Email Type
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<SelectConform
					meta={fields.ReporterEmailType}
					items={emailTypes}
					defaultValue={
						isPolicyHolderOrProxy(fields.partyReportingIncident.value)
							? aboutYouData.ReporterEmailType
							: ""
					}
				/>
				{fields.ReporterEmailType.errors && (
					<FieldError>{fields.ReporterEmailType.errors}</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.reportingEmailAddress.id}>
					Email Address
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<InputConform meta={fields.reportingEmailAddress} type="email" />
				{fields.reportingEmailAddress.errors && (
					<FieldError>{fields.reportingEmailAddress.errors}</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.ReporterContactPreference.id}>
					Contact Preference
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<SelectConform
					meta={fields.ReporterContactPreference}
					items={[
						{ value: "mail", name: "Mail" },
						{ value: "email", name: "Email" },
						{ value: "phone", name: "Phone" },
						{ value: "sms", name: "SMS" },
						{ value: "whatsapp", name: "WhatsApp" },
					]}
				/>
				{fields.ReporterContactPreference.errors && (
					<FieldError>
						{fields.ReporterContactPreference.errors}
					</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.ReporterAddressType.id}>
					Address Type
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<SelectConform
					meta={fields.ReporterAddressType}
					items={addressTypes}
				/>
				{fields.ReporterAddressType.errors && (
					<FieldError>{fields.ReporterAddressType.errors}</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.ReporterAddressLine1.id}>
					Address Line 1
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<InputConform meta={fields.ReporterAddressLine1} type="text" />
				{fields.ReporterAddressLine1.errors && (
					<FieldError>{fields.ReporterAddressLine1.errors}</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.ReporterAddressLine2.id}>
					Address Line 2
				</Label>
				<InputConform meta={fields.ReporterAddressLine2} type="text" />
				{fields.ReporterAddressLine2.errors && (
					<FieldError>{fields.ReporterAddressLine2.errors}</FieldError>
				)}
			</Field>

			<Field className="mb-4">
				<Label htmlFor={fields.ReporterCountry.id}>
					Country
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<ComboboxConform
					meta={fields.ReporterCountry}
					items={countries}
					placeholderNoResults="Couldn't find country."
					placeholderSearch="Search Countries"
				/>
				{fields.ReporterCountry.errors && (
					<FieldError>{fields.ReporterCountry.errors}</FieldError>
				)}
			</Field>

			{(fields.ReporterCountry.value === "barbados" ||
				fields.ReporterCountry.value === "jamaica") && (
				<Field className="mb-4">
					<Label htmlFor={fields.ReporterParish.id}>
						Parish
						<Asterisk
							size={16}
							color="#ff1414"
							className="inline relative top-[-3px]"
						/>
					</Label>
					<SelectConform
						meta={fields.ReporterParish}
						items={
							fields.ReporterCountry.value === "barbados"
								? bbParishes
								: jmParishes
						}
						handleChange={() => {
							form.update({ name: "ReporterDistrict", value: "" });
						}}
					/>
					{fields.ReporterParish.errors && (
						<FieldError>{fields.ReporterParish.errors}</FieldError>
					)}
				</Field>
			)}

			{fields.ReporterCountry.value === "barbados" &&
				fields.ReporterParish.value && (
					<Field className="mb-4">
						<Label htmlFor={fields.ReporterDistrict.id}>District</Label>
						<ComboboxConform
							meta={fields.ReporterDistrict}
							items={districts}
							placeholderNoResults="Couldn't find district"
							placeholderSearch="Search Districts"
						/>
						{fields.ReporterDistrict.errors && (
							<FieldError>{fields.ReporterDistrict.errors}</FieldError>
						)}
					</Field>
				)}

			<Field className="mb-4">
				<Label htmlFor={fields.AreYouDriver.id}>
					Were you the driver?
					<Asterisk
						size={16}
						color="#ff1414"
						className="inline relative top-[-3px]"
					/>
				</Label>
				<RadioGroupConform
					meta={fields.AreYouDriver as FieldMetadata<string>}
					items={[
						{ value: "yes", label: "Yes" },
						{ value: "no", label: "No" },
					]}
				/>
				{fields.AreYouDriver.errors && (
					<FieldError>{fields.AreYouDriver.errors}</FieldError>
				)}
			</Field>

			<FormButtons hideBack={true} />
		</Form>
	);
}
